import type { VoiceAudioChunk } from "@t3tools/contracts";

/**
 * Read a recorded audio {@link Blob} as a base64 string (without the
 * `data:...;base64,` prefix), suitable for the voice RPC payloads which
 * transport audio as base64 text.
 */
export async function readBlobAsBase64(blob: Blob): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener(
      "error",
      () => reject(reader.error ?? new Error("Failed to read voice recording.")),
      { once: true },
    );
    reader.addEventListener("load", () => resolve(String(reader.result ?? "")), { once: true });
    reader.readAsDataURL(blob);
  });
  const commaIndex = dataUrl.indexOf(",");
  return commaIndex === -1 ? dataUrl : dataUrl.slice(commaIndex + 1);
}

/** Pick the best-supported mime type for microphone capture in this browser. */
export function preferredVoiceRecordingMimeType(): string {
  if (typeof MediaRecorder === "undefined") return "audio/webm";
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
  return candidates.find((candidate) => MediaRecorder.isTypeSupported(candidate)) ?? "audio/webm";
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes.buffer;
}

/**
 * Records a single push-to-talk turn from the microphone.
 *
 * `start()` acquires the mic and begins recording; `stop()` resolves with the
 * captured audio as a base64 string plus its mime type, and releases the mic.
 * `cancel()` discards the recording and releases the mic without resolving.
 */
export class VoiceTurnRecorder {
  private recorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private chunks: Blob[] = [];

  get isRecording(): boolean {
    return this.recorder !== null && this.recorder.state === "recording";
  }

  async start(): Promise<void> {
    if (this.isRecording) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      throw new Error("This browser does not expose microphone recording APIs.");
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mimeType = preferredVoiceRecordingMimeType();
    const recorder = MediaRecorder.isTypeSupported(mimeType)
      ? new MediaRecorder(stream, { mimeType })
      : new MediaRecorder(stream);
    this.stream = stream;
    this.recorder = recorder;
    this.chunks = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) this.chunks.push(event.data);
    };
    recorder.start();
  }

  async stop(): Promise<{ data: string; mimeType: string } | null> {
    const recorder = this.recorder;
    if (!recorder) return null;
    const stopped = new Promise<void>((resolve) => {
      recorder.addEventListener("stop", () => resolve(), { once: true });
    });
    if (recorder.state !== "inactive") recorder.stop();
    await stopped;
    const mimeType = recorder.mimeType || "audio/webm";
    const blob = new Blob(this.chunks, { type: mimeType });
    this.release();
    if (blob.size === 0) return null;
    return { data: await readBlobAsBase64(blob), mimeType };
  }

  cancel(): void {
    const recorder = this.recorder;
    if (recorder && recorder.state !== "inactive") {
      recorder.ondataavailable = null;
      recorder.stop();
    }
    this.release();
  }

  private release(): void {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
    this.recorder = null;
    this.chunks = [];
  }
}

/**
 * Plays a stream of base64 {@link VoiceAudioChunk}s gaplessly through a Web
 * `AudioContext`.
 *
 * Decoding is guarded: the placeholder voice backend emits base64-encoded text
 * rather than real audio, so `decodeAudioData` will reject. Such chunks are
 * dropped silently so the UI stays quiet (rather than erroring) until a real
 * TTS provider is configured.
 */
export class VoiceAudioPlayer {
  private context: AudioContext | null = null;
  private nextStartTime = 0;
  private disposed = false;

  private ensureContext(): AudioContext | null {
    if (this.disposed) return null;
    if (this.context) return this.context;
    const Ctor =
      typeof window === "undefined"
        ? undefined
        : (window.AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
    if (!Ctor) return null;
    this.context = new Ctor();
    return this.context;
  }

  async enqueue(chunk: VoiceAudioChunk): Promise<void> {
    const context = this.ensureContext();
    if (!context) return;
    if (context.state === "suspended") {
      await context.resume().catch(() => {});
    }
    let buffer: AudioBuffer;
    try {
      buffer = await context.decodeAudioData(base64ToArrayBuffer(chunk.data));
    } catch {
      // Placeholder / non-audio chunk — ignore until a real provider lands.
      return;
    }
    if (this.disposed || !this.context) return;
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(context.destination);
    const startAt = Math.max(context.currentTime, this.nextStartTime);
    source.start(startAt);
    this.nextStartTime = startAt + buffer.duration;
  }

  /** Stop any pending playback and reset the schedule (e.g. on interruption). */
  reset(): void {
    this.nextStartTime = 0;
  }

  async dispose(): Promise<void> {
    this.disposed = true;
    this.nextStartTime = 0;
    const context = this.context;
    this.context = null;
    if (context) await context.close().catch(() => {});
  }
}
